import { TAB_SLOT_KEYS, DEFAULT_TAB_KEYS } from '@/data/store/useTabConfigStore';
import { isFreeOrg, isTabKeyAllowedForPlan, getDefaultTabKey } from '@/utils/organizationEntitlements';

export const TAB_OPTIONS = [
    {
        key: 'home',
        label: 'Dashboard',
        route: '/',
        icon: 'home',
        iconOutline: 'home-outline',
        sfDefault: 'house',
        sfSelected: 'house.fill',
        permissionCheck: () => true,
    },
    {
        key: 'search',
        label: 'Search',
        route: '/search',
        icon: 'search',
        iconOutline: 'search-outline',
        sfDefault: 'magnifyingglass',
        sfSelected: 'magnifyingglass',
        permissionCheck: () => true,
    },
    {
        key: 'notifications',
        label: 'Notifications',
        route: '/notifications',
        icon: 'notifications',
        iconOutline: 'notifications-outline',
        sfDefault: 'bell',
        sfSelected: 'bell.fill',
        permissionCheck: () => true,
    },
    {
        key: 'settings',
        label: 'Settings',
        route: '/settings',
        icon: 'settings',
        iconOutline: 'settings-outline',
        sfDefault: 'gearshape',
        sfSelected: 'gearshape.fill',
        permissionCheck: () => true,
    },
    {
        key: 'assistant',
        label: 'Assistant',
        route: '/assistant',
        icon: 'sparkles',
        iconOutline: 'sparkles-outline',
        sfDefault: 'sparkles',
        sfSelected: 'sparkles',
        permissionCheck: (user) => user?.can?.('Use AI Assistant'),
    },
    {
        key: 'people',
        label: 'People',
        route: '/people',
        icon: 'person',
        iconOutline: 'person-outline',
        sfDefault: 'person',
        sfSelected: 'person.fill',
        permissionCheck: (user) => user?.can?.('View People'),
    },
    {
        key: 'events',
        label: 'Events',
        route: '/events',
        icon: 'calendar',
        iconOutline: 'calendar-outline',
        sfDefault: 'calendar',
        sfSelected: 'calendar.fill',
        permissionCheck: () => true,
    },
    {
        key: 'tasks',
        label: 'Tasks',
        route: '/tasks',
        icon: 'checkbox',
        iconOutline: 'checkbox-outline',
        sfDefault: 'checkmark.square',
        sfSelected: 'checkmark.square.fill',
        permissionCheck: () => true,
    },
    {
        key: 'training',
        label: 'Training',
        route: '/training',
        icon: 'school',
        iconOutline: 'school-outline',
        sfDefault: 'book',
        sfSelected: 'book.fill',
        permissionCheck: (user) => user?.can?.('View Training'),
    },
    {
        // Key stays 'paths' for entitlement/shortcut/tab-config compat; the
        // canonical route is /automations (legacy /paths redirects).
        key: 'paths',
        label: 'Automations',
        route: '/automations',
        icon: 'git-network',
        iconOutline: 'git-network-outline',
        sfDefault: 'arrow.branch',
        sfSelected: 'arrow.branch',
        permissionCheck: (user) => user?.can?.('View Paths'),
    },
    {
        // Separate key from 'paths': same 'paths' feature entitlement, but a
        // distinct tab slot so Follow Up can sit alongside Automations.
        key: 'followUp',
        label: 'Follow Up',
        route: '/follow-up',
        icon: 'user-round-arrow-left',
        iconOutline: 'user-round-arrow-left',
        sfDefault: 'person.and.arrow.left.and.arrow.right',
        sfSelected: 'person.fill.and.arrow.left.and.arrow.right',
        permissionCheck: (user) => user?.can?.('View Follow Up'),
    },
    {
        key: 'forms',
        label: 'Forms',
        route: '/forms',
        icon: 'document-text',
        iconOutline: 'document-text-outline',
        sfDefault: 'doc.text',
        sfSelected: 'doc.text.fill',
        // 'View Forms' permission was removed — every org member can see
        // public forms plus any form they hold a per-form grant on.
        permissionCheck: () => true,
    },
    {
        key: 'files',
        label: 'Files',
        route: '/files',
        icon: 'folder',
        iconOutline: 'folder-outline',
        sfDefault: 'folder',
        sfSelected: 'folder.fill',
        permissionCheck: (user) => user?.can?.('View Files'),
    },
    {
        key: 'connect',
        label: 'Connect',
        route: '/connect',
        icon: 'chatbubbles',
        iconOutline: 'chatbubbles-outline',
        sfDefault: 'bubble.left.and.bubble.right',
        sfSelected: 'bubble.left.and.bubble.right.fill',
        permissionCheck: (user) => user?.canAny?.(['View Conversations']),
    },
    {
        key: 'contributions',
        label: 'Giving Management',
        shortLabel: 'Giving',
        route: '/contributions',
        icon: 'hand-holding-dollar',
        iconOutline: 'hand-holding-dollar',
        iconLib: 'fa',
        sfDefault: 'dollarsign.circle',
        sfSelected: 'dollarsign.circle.fill',
        permissionCheck: (user) => user?.can?.('View Contributions'),
    },
    {
        key: 'roles',
        label: 'Roles',
        route: '/roles',
        icon: 'people',
        iconOutline: 'people-outline',
        sfDefault: 'person.2',
        sfSelected: 'person.2.fill',
        permissionCheck: (user) => user?.canAny?.(['Create Roles', 'Edit Roles', 'Delete Roles']) || user?.ManagingRoles?.length > 0,
    },
    {
        key: 'registrations',
        label: 'Registration Requests',
        shortLabel: 'Requests',
        route: '/registrations',
        icon: 'person-add',
        iconOutline: 'person-add-outline',
        sfDefault: 'person.badge.plus',
        sfSelected: 'person.badge.plus.fill',
        permissionCheck: (user) => user?.isAdmin,
    },
    {
        key: 'analytics',
        label: 'Reports & Analytics',
        shortLabel: 'Analytics',
        route: '/analytics',
        icon: 'stats-chart',
        iconOutline: 'stats-chart-outline',
        sfDefault: 'chart.bar',
        sfSelected: 'chart.bar.fill',
        permissionCheck: () => true,
    },
    {
        key: 'adminDashboard',
        label: 'Admin Dashboard',
        shortLabel: 'Admin',
        route: '/settings/organization/admin',
        icon: 'shield-checkmark',
        iconOutline: 'shield-checkmark-outline',
        sfDefault: 'shield.checkerboard',
        sfSelected: 'shield.checkerboard',
        permissionCheck: (user) => user?.isAdmin,
    },
    {
        // UI-only rollout flag gates the option entirely; rostered teachers
        // pass via the type's roleID, everyone else needs a module permission.
        key: 'biblestudies',
        label: 'Bible Studies',
        route: '/biblestudies',
        icon: 'book',
        iconOutline: 'book-outline',
        sfDefault: 'book',
        sfSelected: 'book.fill',
        permissionCheck: (user, _entitlements, context) => {
            if (context?.enableBibleStudies !== true) return false;
            const roleID = context?.bibleStudyType?.roleID;
            return !!(user?.isAdmin
                || user?.can?.('Manage Bible Studies')
                || user?.can?.('View Bible Studies')
                || (roleID && user?.Roles?.some(r => r.id === roleID)));
        },
    },
];

export const getTabOption = (key, smallGroupTypes = [], context = null) => {
    const staticOption = TAB_OPTIONS.find((o) => o.key === key);
    if (staticOption) return staticOption;

    if (key?.startsWith('smallgroups:')) {
        const typeId = key.split(':')[1];
        const type = smallGroupTypes.find((t) => String(t.id) === typeId);
        if (type) {
            // Flag-on orgs: a saved tab for a Bible Study type follows the
            // records into the new module instead of dead-ending on the
            // legacy Small Groups screen.
            const isBibleStudyType = context?.enableBibleStudies === true && type.purpose === 'BIBLE_STUDY';
            const icon = type.icon || 'people';
            return {
                key,
                label: type.name,
                shortLabel: type.name.length > 12 ? `${type.name.slice(0, 12)}…` : type.name,
                route: isBibleStudyType ? '/biblestudies' : `/smallgroups?typeID=${type.id}`,
                icon,
                iconOutline: `${icon}-outline`,
                iconLib: 'ionicon',
                sfDefault: 'person.3',
                sfSelected: 'person.3.fill',
                permissionCheck: () => true,
            };
        }
    }

    return TAB_OPTIONS[0];
};

/**
 * Tab options a user may pick from. On Free orgs the plan allowlist intersects
 * the normal permission check, and paid-only dynamic options (small group
 * types) are never offered.
 */
export const getTabOptionsForUser = (user, smallGroupTypes = [], entitlements = null, context = null) => {
    const free = isFreeOrg(entitlements);
    const options = TAB_OPTIONS.filter((o) => {
        if (free && !isTabKeyAllowedForPlan(o.key, entitlements)) return false;
        if (typeof o.permissionCheck === 'function') {
            return o.permissionCheck(user, entitlements, context) !== false;
        }
        return true;
    });

    if (free) return options;

    smallGroupTypes
        ?.filter((type) => (context?.enableBibleStudies === true ? type.purpose !== 'BIBLE_STUDY' : true)
            && (!type.roleID || user?.Roles?.some((r) => r.id === type.roleID) || user?.isAdmin))
        .forEach((type) => {
            options.push({
                key: `smallgroups:${type.id}`,
                label: type.name,
                route: `/smallgroups?typeID=${type.id}`,
                icon: type.icon || 'people',
                iconOutline: `${type.icon || 'people'}-outline`,
                iconLib: 'ionicon',
                sfDefault: 'person.3',
                sfSelected: 'person.3.fill',
                permissionCheck: () => true,
            });
        });

    return options;
};

export const buildTabConfig = (options = [], user, entitlements = null) => {
    const availableKeys = new Set(getTabOptionsForUser(user, [], entitlements).map((o) => o.key));
    const config = {};
    TAB_SLOT_KEYS.forEach((slot) => {
        const selected = options[slot] || DEFAULT_TAB_KEYS[slot];
        config[slot] = availableKeys.has(selected) ? selected : getDefaultTabKey(slot, entitlements, DEFAULT_TAB_KEYS);
    });
    return config;
};
